import { type CoreApp } from '@grafana/data';
import { type DataQuery } from '@grafana/schema';
import { type QuerySpec, useUpdateQueryMutation } from 'app/extensions/api/clients/queries/v1';
import { useQueryLibraryContext } from 'app/features/explore/QueryLibrary/QueryLibraryContext';

import { QueryLibraryInteractions } from '../QueryLibraryAnalyticsEvents';
import { type SavedQueryEditOrigin } from '../utils/savedQueryReturnTo';

export const useQueryLibrarySave = () => {
  const { queryLibraryEnabled, openDrawer, triggerAnalyticsEvent } = useQueryLibraryContext();
  const [updateQueryTemplate] = useUpdateQueryMutation();

  const saveNewQuery = async (
    query: DataQuery,
    onSelectQuery?: (query: DataQuery) => void,
    options?: { context?: CoreApp | string }
  ) => {
    if (!queryLibraryEnabled) {
      return;
    }
    return openDrawer({ onSelectQuery, options, query });
  };

  const updateQuery = async (
    query: DataQuery,
    options?: { editSavedQueryRef?: string; origin?: SavedQueryEditOrigin },
    onSuccess?: () => void
  ) => {
    const { editSavedQueryRef, origin } = options || {};
    if (!editSavedQueryRef || !query || !query.datasource?.type) {
      console.error('Invalid query update attempt:', {
        editSavedQueryRef: !!editSavedQueryRef,
        query: !!query,
        datasourceType: query?.datasource?.type,
      });
      return;
    }
    const targetQuery: QuerySpec = {
      targets: [
        {
          properties: {
            ...query,
            datasource: {
              ...query.datasource,
              type: query.datasource?.type,
            },
          },
          variables: {},
        },
      ],
    };

    try {
      await updateQueryTemplate({
        name: editSavedQueryRef,
        patch: {
          spec: {
            targets: targetQuery.targets,
          },
        },
      }).unwrap();

      // Track successful update. `app` stays 'explore' (where the save happened); `origin` carries the
      // surface that sent the user here, which is otherwise lost on the return leg.
      triggerAnalyticsEvent(QueryLibraryInteractions.updateQueryFromExploreCompleted, {
        datasourceType: query.datasource?.type,
        origin,
      });

      onSuccess?.();
    } catch (error) {
      // Error is already handled by the global error system
      console.error('Failed to update query in library:', error);
    }
  };

  const isEnabled = () => {
    return queryLibraryEnabled;
  };

  return {
    saveNewQuery,
    updateQuery,
    isEnabled,
  };
};
